import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, Typography, FormControl, InputLabel, IconButton, TableContainer, Button, Snackbar, Alert } from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { apiRequest } from '../api/api';

dayjs.locale('it');

// Costanti importate da MioOrario (copiate per isolamento)
const TIPO_ORARIO_OPTIONS = [
  'LAVORATO',
  'CORSI AI DIPENDENTI',
  'CHIUSO DITTA',
  'RIPOSO',
  'ROL',
  'FERIE',
  'MALATTIA',
  "MATERNITA'",
  'LEGGE 104 PERMESSO',
  'AVIS',
  'ALLATTAMENTO',
  'LUTTO',
  'PERMESSI PARENTALI',
  'SMART WORKING',
  'CONGEDO MATRIMONIALE',
  'PERMESSI ELETTORALI',
  'ASPETTATIVA',
  'PERMESSI STUDIO',
  'CHIUSO ORDINANAZA',
  'QUARANTENA',
  'ASSENZA N.R. DL 127',
  'ASSENZA NON RETRIBUITA',
  'LEGGE 118 PERMESSO'
];
const TIPO_ASSENZA_LIST = [
  'CORSI AI DIPENDENTI', 'CHIUSO DITTA', 'ROL', 'FERIE', 'MALATTIA', "MATERNITA'", 'LEGGE 104 PERMESSO',
  'AVIS', 'ALLATTAMENTO', 'LUTTO', 'PERMESSI PARENTALI', 'CONGEDO MATRIMONIALE', 'PERMESSI ELETTORALI',
  'ASPETTATIVA', 'PERMESSI STUDIO', 'CHIUSO ORDINANAZA', 'QUARANTENA', 'ASSENZA N.R. DL 127', 'ASSENZA NON RETRIBUITA', 'LEGGE 118 PERMESSO'
];
const isAssenzaTipo = (t?: string | null) => !!t && TIPO_ASSENZA_LIST.includes(t);

interface TurnoCRecord {
  id: number;
  negPrev: string;
  negCons: string;
  descrizione: string;
  data: string; // ISO date
  // Campi Prev non usati dalla coordinatrice ma mantenuti nel modello per compatibilità backend
  oraPrevInizio: string | null;
  oraPrevFine: string | null;
  turnoPrev: string;
  // Campi Cons effettivamente mostrati/editabili
  turnoCons: string;
  oraConsInizio: string | null;
  oraConsFine: string | null;
  codiceTipoOrario?: string;
  annotazioni?: string;
  giornoSettimana: string;
}

interface Negozio { neg: string; negozio: string; }

const TURNO_OPTIONS = ['Apertura','Mattina', 'Intermezzo', 'Pomeriggio', 'Chiusura', 'Riposo'];

const MioOrarioC: React.FC<{ dipendenteNome: string }> = ({ dipendenteNome }) => {
  const [mese, setMese] = useState<number>(dayjs().month() + 1);
  const [anno, setAnno] = useState<number>(dayjs().year());
  const [records, setRecords] = useState<TurnoCRecord[]>([]);
  const [negozi, setNegozi] = useState<Negozio[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setSavingId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fit all rows into visible viewport
  // Scroll container (TableContainer)
  const paperRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLTableSectionElement | null>(null);
  const [paperHeight, setPaperHeight] = useState<number>(0);
  // Keep rows readable: controls need ~36px at minimum
  const [rowHeight, setRowHeight] = useState<number>(26);
  // Keep latest records for debounced autosave
  const recordsRef = useRef<TurnoCRecord[]>([]);
  useEffect(() => { recordsRef.current = records; }, [records]);
  const saveTimers = useRef<Record<number, any>>({});
  const schedulePersist = (id: number, delay = 600) => {
    const timers = saveTimers.current;
    if (timers[id]) clearTimeout(timers[id]);
    timers[id] = setTimeout(() => {
      const row = recordsRef.current.find(r => r.id === id);
      if (row) persistRow(row);
    }, delay);
  };

  const recalcHeights = () => {
    if (!paperRef.current) return;
    // Compute available height from viewport bottom to the top of the paper
    const rect = paperRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    // Leave a small bottom margin (8px)
    const available = Math.max(100, Math.floor(viewportH - rect.top - 8));
    setPaperHeight(available);
    const headerH = headRef.current ? headRef.current.getBoundingClientRect().height : 0;
    const bodyAvail = Math.max(0, available - headerH);
    const rows = Math.max(1, records.length);
    // Compute row height to make rows fit; keep a minimum of 22px for readability
    const computed = Math.max(22, Math.floor(bodyAvail / rows));
    setRowHeight(computed);
  };

  useLayoutEffect(() => {
    const ro = new ResizeObserver(() => recalcHeights());
    if (paperRef.current) ro.observe(paperRef.current);
    window.addEventListener('resize', recalcHeights);
    // Initial
    setTimeout(recalcHeights, 0);
    return () => {
      try { ro.disconnect(); } catch {}
      window.removeEventListener('resize', recalcHeights);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { recalcHeights(); /* re-eval when records change */ }, [records.length]);

  const loadMonth = async () => {
    setLoading(true);
    try {
      // 1) genera griglia vuota lato client per tutto il mese
      const monthStart = dayjs(`${anno}-${String(mese).padStart(2,'0')}-01`).startOf('month');
      const monthEnd = monthStart.endOf('month');
      const empty: TurnoCRecord[] = [];
      let cursor = monthStart.clone();
      while (cursor.isBefore(monthEnd) || cursor.isSame(monthEnd,'day')) {
        const iso = cursor.format('YYYY-MM-DD');
        empty.push({
          id: -parseInt(cursor.format('YYYYMMDD')),
          negPrev: '',
          negCons: '',
          descrizione: dipendenteNome,
          data: iso,
          oraPrevInizio: null,
          oraPrevFine: null,
          turnoPrev: '',
          turnoCons: '',
          oraConsInizio: null,
          oraConsFine: null,
          codiceTipoOrario: '',
          annotazioni: '',
          giornoSettimana: cursor.format('dddd')
        });
        cursor = cursor.add(1,'day');
      }
      // 2) carica eventuali dati reali dal backend (se esistono)
      const server = await apiRequest<TurnoCRecord[]>(`/coordinatore/turni-c/mese?descrizione=${encodeURIComponent(dipendenteNome)}&mese=${mese}&anno=${anno}`);
      // 3) merge: per ogni giorno, se ci sono N righe reali usale tutte, altrimenti 1 placeholder
      const merged: TurnoCRecord[] = [];
      const byDate: Record<string, TurnoCRecord[]> = {};
      server.forEach(s => { (byDate[s.data] ||= []).push(s); });
      empty.forEach(ph => {
        const reals = byDate[ph.data];
        if (reals && reals.length) {
          merged.push(...reals.sort((a,b) => (a.id - b.id)));
        } else {
          merged.push(ph);
        }
      });
      setRecords(merged.sort((a,b) => a.data.localeCompare(b.data)));
    } catch (e) {
      console.error('Errore caricamento mese', e);
      // in caso di errore, mostra comunque la griglia vuota del mese
      const monthStart = dayjs(`${anno}-${String(mese).padStart(2,'0')}-01`).startOf('month');
      const monthEnd = monthStart.endOf('month');
      const empty: TurnoCRecord[] = [];
      let cursor = monthStart.clone();
      while (cursor.isBefore(monthEnd) || cursor.isSame(monthEnd,'day')) {
        empty.push({
          id: -parseInt(cursor.format('YYYYMMDD')),
          negPrev: '',
          negCons: '',
          descrizione: dipendenteNome,
          data: cursor.format('YYYY-MM-DD'),
          oraPrevInizio: null,
          oraPrevFine: null,
          turnoPrev: '',
          turnoCons: '',
          oraConsInizio: null,
          oraConsFine: null,
          codiceTipoOrario: '',
          annotazioni: '',
          giornoSettimana: cursor.format('dddd')
        });
        cursor = cursor.add(1,'day');
      }
      setRecords(empty);
    } finally {
      setLoading(false);
    }
  };

  // Helpers per gestione righe multiple per giorno
  // Una riga è "Originale" se è la prima del giorno
  const isFirstOfDay = (index: number) => index === 0 || records[index - 1].data !== records[index].data;
  
  // Valida se tutti i campi obbligatori sono compilati (eccetto annotazioni)
  const validateRow = (row: TurnoCRecord): { valid: boolean; error?: string } => {
    if (!row.negPrev || row.negPrev.trim() === '') {
      return { valid: false, error: 'Neg_Prev obbligatorio' };
    }
    if (!row.turnoPrev || row.turnoPrev.trim() === '') {
      return { valid: false, error: 'Turno_Prev obbligatorio' };
    }
    if (!row.negCons || row.negCons.trim() === '') {
      return { valid: false, error: 'Neg_Cons obbligatorio' };
    }
    if (!row.oraConsInizio) {
      return { valid: false, error: 'Ora_Cons_Inizio obbligatoria' };
    }
    if (!row.oraConsFine) {
      return { valid: false, error: 'Ora_Cons_Fine obbligatoria' };
    }
    if (!row.turnoCons || row.turnoCons.trim() === '') {
      return { valid: false, error: 'Turno_Cons obbligatorio' };
    }
    if (!row.codiceTipoOrario || row.codiceTipoOrario.trim() === '') {
      return { valid: false, error: 'CODICE_TIPO_ORARIO obbligatorio' };
    }
    return { valid: true };
  };

  // Salva il turno su GDV_OUTBOX_EVENT
  const handleSalvaOutbox = async (rowId: number) => {
    const row = records.find(r => r.id === rowId);
    if (!row) return;

    // Validazione
    const validation = validateRow(row);
    if (!validation.valid) {
      setSnackbar({
        open: true,
        message: `Validazione fallita: ${validation.error}`,
        severity: 'error'
      });
      return;
    }

    // Se l'ID è negativo (riga non salvata), salva prima il turno
    if (row.id < 0) {
      setSnackbar({
        open: true,
        message: 'Devi prima salvare il turno prima di inviarlo al sistema esterno',
        severity: 'error'
      });
      return;
    }

    try {
      await apiRequest(`/coordinatore/turni-c/${row.id}/salva-outbox`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      setSnackbar({
        open: true,
        message: 'Turno salvato su outbox con successo',
        severity: 'success'
      });
    } catch (err: any) {
      const errorMessage = err?.message || 'Errore nel salvataggio su outbox';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      console.error('Errore salvataggio outbox:', err);
    }
  };

  const addRowAfter = (rowIndex: number) => {
    const ref = records[rowIndex];
    const newId = -Math.floor(Date.now() % 1000000000) - Math.floor(Math.random()*1000);
    const newRow: TurnoCRecord = {
      id: newId,
      negPrev: '',
      negCons: '',
      descrizione: dipendenteNome,
      data: ref.data,
      oraPrevInizio: null,
      oraPrevFine: null,
      turnoPrev: '',
      turnoCons: '',
      oraConsInizio: null,
      oraConsFine: null,
      codiceTipoOrario: '',
      annotazioni: '',
      giornoSettimana: dayjs(ref.data).format('dddd')
    };
    // inserisci dopo l'ultimo della stessa data
    const lastSameDateIndex = (() => {
      let last = rowIndex;
      for (let i = rowIndex + 1; i < records.length; i++) {
        if (records[i].data === ref.data) last = i; else break;
      }
      return last;
    })();
    setRecords(prev => {
      const arr = [...prev];
      arr.splice(lastSameDateIndex + 1, 0, newRow);
      return arr;
    });
  };
  const deleteRow = async (rowIndex: number) => {
    const row = records[rowIndex];
    const first = isFirstOfDay(rowIndex);
    const created = row.id < 0;
    if (first) {
      // Svuota la riga "Originale" mantenendo Data e Giorno
      const cleared: TurnoCRecord = {
        ...row,
        negPrev: '',
        turnoPrev: '',
        negCons: '',
        oraConsInizio: null,
        oraConsFine: null,
        turnoCons: '',
        codiceTipoOrario: '',
        annotazioni: ''
      };
      setRecords(prev => prev.map((r, i) => (i === rowIndex ? cleared : r)));
      // persisti lo svuotamento (autosave farà comunque da safety net)
      try { await persistRow(cleared); } catch {}
      return;
    }
    // Righe aggiunte: rimuovi
    try {
      if (!created && row.id > 0) {
        await apiRequest(`/coordinatore/turni-c/${row.id}`, { method: 'DELETE' });
      }
    } catch (e) {
      console.error('Errore eliminazione riga', e);
    } finally {
      setRecords(prev => prev.filter((_, i) => i !== rowIndex));
    }
  };

  // Navigazione con tastiera (frecce e TAB)
  const fieldsOrder: (keyof TurnoCRecord)[] = ['negPrev','turnoPrev','negCons','oraConsInizio','oraConsFine','turnoCons','codiceTipoOrario','annotazioni'];
  const focusCell = (rowIdx: number, field: keyof TurnoCRecord) => {
    const el = document.querySelector(`[data-row="${rowIdx}"][data-field="${field}"]`) as HTMLElement | null;
    el?.focus();
    // no-op: keeping focus only
  };
  const handleKeyNav = (e: React.KeyboardEvent, rowIdx: number, field: keyof TurnoCRecord) => {
    const key = e.key;
    const col = fieldsOrder.indexOf(field);
    let nextRow = rowIdx;
    let nextCol = col;
    const maxRow = records.length - 1;
    if (key === 'ArrowRight' || (key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      if (col < fieldsOrder.length - 1) nextCol = col + 1; else { nextCol = 0; nextRow = Math.min(maxRow, rowIdx + 1); }
    } else if (key === 'ArrowLeft' || (key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      if (col > 0) nextCol = col - 1; else { nextCol = fieldsOrder.length - 1; nextRow = Math.max(0, rowIdx - 1); }
    } else if (key === 'ArrowDown') {
      e.preventDefault();
      nextRow = Math.min(maxRow, rowIdx + 1);
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      nextRow = Math.max(0, rowIdx - 1);
    } else {
      return;
    }
    const targetField = fieldsOrder[nextCol];
    focusCell(nextRow, targetField);
  };

  const loadNegozi = async () => {
    try {
      const list = await apiRequest<Negozio[]>(`/negozi/accessibili`); // Controller esistente con filtro ruolo
      setNegozi(list);
    } catch (e) {
      console.error('Errore caricamento negozi', e);
    }
  };

  useEffect(() => { loadNegozi(); }, []);
  useEffect(() => { loadMonth(); }, [mese, anno, dipendenteNome]);

  const updateField = (id: number, field: keyof TurnoCRecord, value: any) => {
    setRecords(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    schedulePersist(id);
  };

  const persistRow = async (row: TurnoCRecord) => {
    setSavingId(row.id);
    try {
      // Regola: se tipo orario è assenza, turnoCons = 'Assenza'
      const normalizeTurnoCons = (t?: string) => t === 'Apertura' ? 'Mattina' : t === 'Chiusura' ? 'Pomeriggio' : (t || '');
      const payload = { ...row, turnoCons: isAssenzaTipo(row.codiceTipoOrario || '') ? 'Assenza' : normalizeTurnoCons(row.turnoCons) };
      if (row.id < 0) {
        // crea nuovo record
        const created = await apiRequest<TurnoCRecord>(`/coordinatore/turni-c`, {
          method: 'POST',
          body: JSON.stringify({
            negPrev: payload.negPrev || '',
            negCons: payload.negCons || '',
            descrizione: payload.descrizione,
            data: payload.data,
            oraPrevInizio: payload.oraPrevInizio,
            oraPrevFine: payload.oraPrevFine,
            turnoPrev: payload.turnoPrev,
            turnoCons: payload.turnoCons,
            oraConsInizio: payload.oraConsInizio,
            oraConsFine: payload.oraConsFine,
            codiceTipoOrario: payload.codiceTipoOrario || '',
            annotazioni: payload.annotazioni || ''
          })
        });
        // rimpiazza placeholder con record reale
        setRecords(prev => prev.map(r => r.id === row.id ? created : r));
      } else {
        await apiRequest(`/coordinatore/turni-c/${row.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      }
    } catch (e) {
      console.error('Errore salvataggio riga', e);
    } finally {
      setSavingId(null);
    }
  };

  const formatData = (iso: string) => dayjs(iso).format('DD/MM/YYYY');
  const formatWeekday = (iso: string) => dayjs(iso).format('ddd'); // abbreviazione (lun, mar, ...)
  
  // Recap a destra: conteggi per negozio e turno.
  // Regole richieste:
  // 1) Ordinamento negozi ASC
  // 2) Conta per ciascuna colonna (Mattina, Apertura, Intermezzo, Pomeriggio, Chiusura)
  //    usando Turno_Cons se presente (!= ''), altrimenti Turno_Prev.
  //    Se si conta Turno_Cons si usa Neg_Cons, se si conta Turno_Prev si usa Neg_Prev.
  // 3) Ignora le assenze (tipo orario assenza o turno = 'Assenza').
  type RecapRow = { neg: string; Mattina: number; Apertura: number; Intermezzo: number; Pomeriggio: number; Chiusura: number };
  const recapData: RecapRow[] = React.useMemo(() => {
    const zero = () => ({ Mattina: 0, Apertura: 0, Intermezzo: 0, Pomeriggio: 0, Chiusura: 0 });
    const map: Record<string, RecapRow> = {};
    // Pre-inizializza negozi accessibili
    for (const n of negozi) map[n.neg] = { neg: n.neg, ...zero() } as RecapRow;
    for (const r of records) {
      // Determina fonte (Cons vs Prev)
      const hasCons = !!(r.turnoCons && r.turnoCons.trim());
      const turnoEff = hasCons ? r.turnoCons.trim() : (r.turnoPrev ? r.turnoPrev.trim() : '');
      const negEff = hasCons ? (r.negCons || '').trim() : (r.negPrev || '').trim();
      if (!turnoEff || !negEff) continue;
      if (isAssenzaTipo(r.codiceTipoOrario || '') || turnoEff === 'Assenza') continue;
      if (!map[negEff]) map[negEff] = { neg: negEff, ...zero() } as RecapRow;
      if (['Mattina', 'Apertura', 'Intermezzo', 'Pomeriggio', 'Chiusura'].includes(turnoEff)) {
        (map[negEff][turnoEff as keyof RecapRow] as number)++;
      }
    }
    return Object.values(map).sort((a,b) => a.neg.localeCompare(b.neg));
  }, [records, negozi]);

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Orario Coordinatore - {dipendenteNome}</Typography>
      <Box display="flex" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel shrink={true}>Mese</InputLabel>
          <select aria-label="Mese" value={mese} onChange={(e) => setMese(Number((e.target as HTMLSelectElement).value))}
                  style={{ padding: '6px', fontSize: '0.85rem' }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel shrink={true}>Anno</InputLabel>
          <select aria-label="Anno" value={anno} onChange={(e) => setAnno(Number((e.target as HTMLSelectElement).value))}
                  style={{ padding: '6px', fontSize: '0.85rem' }}>
            {Array.from({ length: 5 }, (_, i) => dayjs().year() - 2 + i).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </FormControl>
        <Button variant="outlined" onClick={loadMonth} disabled={loading}>Ricarica</Button>
      </Box>
      <Box display="flex" gap={2}>
        <TableContainer component={Paper} ref={paperRef} sx={{ height: paperHeight ? `${paperHeight}px` : 'auto', flex: 1 }}>
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& th, & td': { py: 0.3, px: 0.75 }, '& th': { fontSize: '0.78rem' }, '& td': { fontSize: '0.8rem' }, whiteSpace: 'nowrap' }}>
          <colgroup>
            <col style={{ width: 64 }} />
            <col style={{ width: 88 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 104 }} />
            <col style={{ width: 160 }} />
            <col />
            <col style={{ width: 96 }} />
          </colgroup>
          <TableHead ref={headRef}>
            <TableRow>
              <TableCell>Giorno</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Neg_Prev</TableCell>
              <TableCell>Turno_Prev</TableCell>
              <TableCell>Neg_Cons</TableCell>
              <TableCell>Ora_Cons_Inizio</TableCell>
              <TableCell>Ora_Cons_Fine</TableCell>
              <TableCell>Turno_Cons</TableCell>
              <TableCell>CODICE_TIPO_ORARIO</TableCell>
              <TableCell>Annotazioni</TableCell>
              <TableCell>Azione</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r, idx) => (
              <TableRow key={`${r.id}-${idx}`} sx={{ height: `${rowHeight}px` }}>
                <TableCell sx={{ verticalAlign: 'middle', fontWeight: 500 }}>{formatWeekday(r.data)}</TableCell>
                <TableCell sx={{ verticalAlign: 'middle' }}>{formatData(r.data)}</TableCell>
                <TableCell>
                  <select value={r.negPrev} onChange={e => updateField(r.id, 'negPrev', e.target.value)}
                          onKeyDown={(e) => handleKeyNav(e, idx, 'negPrev')} data-row={idx} data-field="negPrev"
                          style={{ width: '100%', fontSize: '0.78rem' }}>
                    <option value="">-</option>
                    {negozi.map(n => <option key={n.neg} value={n.neg}>{n.neg}</option>)}
                  </select>
                </TableCell>
                <TableCell>
                  <select value={r.turnoPrev} onChange={e => updateField(r.id, 'turnoPrev', e.target.value)}
                          onKeyDown={(e) => handleKeyNav(e, idx, 'turnoPrev')} data-row={idx} data-field="turnoPrev"
                          style={{ width: '100%', fontSize: '0.78rem' }}>
                    <option value="">-</option>
                    {TURNO_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </TableCell>
                <TableCell>
                  <select value={r.negCons} onChange={e => updateField(r.id, 'negCons', e.target.value)}
                          onKeyDown={(e) => handleKeyNav(e, idx, 'negCons')} data-row={idx} data-field="negCons"
                          style={{ width: '100%', fontSize: '0.78rem' }}>
                    <option value="">-</option>
                    {negozi.map(n => <option key={n.neg} value={n.neg}>{n.neg}</option>)}
                  </select>
                </TableCell>
                <TableCell>
                  <input
                    type="time"
                    value={r.oraConsInizio || ''}
                    onChange={e => updateField(r.id, 'oraConsInizio', e.target.value || null)}
                    onKeyDown={(e) => handleKeyNav(e, idx, 'oraConsInizio')}
                    data-row={idx} data-field="oraConsInizio"
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.78rem' }}
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="time"
                    value={r.oraConsFine || ''}
                    onChange={e => updateField(r.id, 'oraConsFine', e.target.value || null)}
                    onKeyDown={(e) => handleKeyNav(e, idx, 'oraConsFine')}
                    data-row={idx} data-field="oraConsFine"
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.78rem' }}
                  />
                </TableCell>
                <TableCell>
                  <select value={isAssenzaTipo(r.codiceTipoOrario || '') ? 'Assenza' : (r.turnoCons || '')}
                          disabled={isAssenzaTipo(r.codiceTipoOrario || '')}
                          onChange={e => updateField(r.id, 'turnoCons', e.target.value)}
                          onKeyDown={(e) => handleKeyNav(e, idx, 'turnoCons')} data-row={idx} data-field="turnoCons"
                          style={{ width: '100%', fontSize: '0.78rem' }}>
                    <option value="">-</option>
                    {TURNO_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    <option value="Assenza">Assenza</option>
                  </select>
                </TableCell>
                <TableCell>
                  <select value={r.codiceTipoOrario || ''}
                          onChange={e => updateField(r.id, 'codiceTipoOrario', e.target.value)}
                          onKeyDown={(e) => handleKeyNav(e, idx, 'codiceTipoOrario')} data-row={idx} data-field="codiceTipoOrario"
                          style={{ width: '100%', fontSize: '0.78rem' }}>
                    <option value="">-</option>
                    {TIPO_ORARIO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </TableCell>
                <TableCell>
                  <input
                    type="text"
                    value={r.annotazioni || ''}
                    placeholder="Annotazioni"
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.78rem' }}
                    onChange={e => updateField(r.id, 'annotazioni', e.target.value)}
                    onKeyDown={(e) => handleKeyNav(e, idx, 'annotazioni')}
                    data-row={idx} data-field="annotazioni"
                  />
                </TableCell>
                <TableCell>
                  {(() => {
                    const first = isFirstOfDay(idx);
                    const created = r.id < 0;
                    const canDel = created || first; // cestino su Originale (svuota) e su righe create (elimina)
                    const validation = validateRow(r);
                    const canSave = validation.valid && r.id > 0; // Salva solo se valido e già salvato nel DB
                    
                    return (
                      <Box display="flex" gap={0.5} sx={{ width: '100%', justifyContent: 'center' }}>
                        <IconButton 
                          size="small" 
                          title={canSave ? "Salva su outbox" : (r.id < 0 ? "Salva prima il turno" : validation.error)} 
                          onClick={() => handleSalvaOutbox(r.id)}
                          disabled={!canSave}
                          sx={{ color: canSave ? '#4caf50' : '#ccc' }}
                        >
                          <Save fontSize="small" />
                        </IconButton>
                        {first && (
                          <IconButton size="small" title="Inserisci riga" onClick={() => addRowAfter(idx)}>
                            <Add fontSize="small" />
                          </IconButton>
                        )}
                        {canDel && (
                          <IconButton size="small" title="Elimina riga" onClick={() => deleteRow(idx)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
        {/* Recap a destra */}
        <Paper sx={{ width: 360, height: paperHeight ? `${paperHeight}px` : 'auto', overflow: 'auto', p: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Recap turni (Neg DESC)</Typography>
          <Table size="small" stickyHeader sx={{ '& th, & td': { py: 0.4, px: 0.6 }, '& th': { fontSize: '0.78rem' }, '& td': { fontSize: '0.78rem' }, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 90 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 70 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>Neg</TableCell>
                <TableCell>Mattina</TableCell>
                <TableCell>Apertura</TableCell>
                <TableCell>Intermezzo</TableCell>
                <TableCell>Pomeriggio</TableCell>
                <TableCell>Chiusura</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recapData.map(r => (
                <TableRow key={r.neg}>
                  <TableCell>{r.neg}</TableCell>
                  <TableCell>{r.Mattina}</TableCell>
                  <TableCell>{r.Apertura}</TableCell>
                  <TableCell>{r.Intermezzo}</TableCell>
                  <TableCell>{r.Pomeriggio}</TableCell>
                  <TableCell>{r.Chiusura}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Snackbar per feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MioOrarioC;
